import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import superAdminApiClient from '../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { getLocalDateString } from '../../utils/dateUtils';
import { Modal } from 'react-bootstrap';

const RegisteredCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        name: '',
        mobile: '',
        email: '',
        location: '',
        startDate: '',
        endDate: ''
    });

    const fetchCustomers = useCallback(async (currentPage = 1, currentFilters = filters) => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit,
                ...currentFilters
            };
            
            const response = await superAdminApiClient.get(`/superadmin/registered-customers`, {
                params
            });

            if (response.data.success) {
                setCustomers(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch registered customers');
        } finally {
            setLoading(false);
        }
    }, [limit, filters]);

    const fetchRef = useRef(fetchCustomers);
    useEffect(() => {
        fetchRef.current = fetchCustomers;
    }, [fetchCustomers]);

    const debouncedFetchCustomers = useMemo(
        () => debounce((currentPage, currentFilters) => {
            fetchRef.current(currentPage, currentFilters);
        }, 500),
        []
    );

    useEffect(() => {
        debouncedFetchCustomers(page, filters);
        return () => debouncedFetchCustomers.cancel();
    }, [page, filters, debouncedFetchCustomers]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPage(1); // Reset to page 1 on filter change
    };

    const handleDeleteClick = (customer) => {
        setCustomerToDelete(customer);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        if (isDeleting) return;
        setShowDeleteModal(false);
        setCustomerToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!customerToDelete) return;
        setIsDeleting(true);
        try {
            const response = await superAdminApiClient.delete(`/superadmin/registered-customers/${customerToDelete.id}`);
            if (response.data.success) {
                toast.success(response.data.message || 'Customer deleted permanently successfully');
                setShowDeleteModal(false);
                setCustomerToDelete(null);
                fetchCustomers(page, filters);
            }
        } catch (error) {
            console.error('Error deleting customer:', error);
            toast.error(error.response?.data?.message || 'Failed to delete customer');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            const params = {
                ...filters,
                export: 'true'
            };

            const response = await superAdminApiClient.get(`/superadmin/registered-customers`, {
                params
            });

            if (response.data.success) {
                const exportData = response.data.data;
                if (exportData.length === 0) {
                    toast.info('No data available to export');
                    return;
                }

                // Create CSV
                let csvContent = "Sr. No,Customer Name,Mobile,Email ID,Location,Date\n";
                
                exportData.forEach((row, index) => {
                    const name = row.name ? `"${row.name.replace(/"/g, '""')}"` : 'Guest';
                    const mobile = row.phone || '';
                    const email = row.email ? `"${row.email.replace(/"/g, '""')}"` : '';
                    const location = row.location ? `"${row.location.replace(/"/g, '""')}"` : '';
                    const date = row.createdAt ? format(new Date(row.createdAt), 'dd-MM-yyyy') : '';
                    
                    csvContent += `${index + 1},${name},${mobile},${email},${location},${date}\n`;
                });

                // Trigger download
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", `registered_customers_${format(new Date(), 'dd-MM-yyyy')}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Error downloading data:', error);
            toast.error('Failed to download data');
        }
    };

    return (
        <div className="row">
            <div className="col-lg-12">
                
                {/* Page Title */}
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0">Registered Customers</h4>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="card">
                    <div className="card-header border-0 pb-0">
                        <div className="row align-items-center g-3">
                            <div className="col-sm-auto ms-auto">
                                <button className="btn btn-success" onClick={handleDownloadExcel}>
                                    <i className="ri-file-excel-2-line align-bottom me-1"></i> Download Excel (CSV)
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card-body border border-dashed border-end-0 border-start-0 mt-3">
                        <div className="row g-3">
                            <div className="col-xxl-2 col-sm-4">
                                <label className="form-label">Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="name"
                                    value={filters.name}
                                    onChange={handleFilterChange}
                                    placeholder="Search name"
                                />
                            </div>
                            <div className="col-xxl-2 col-sm-4">
                                <label className="form-label">Mobile</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="mobile"
                                    value={filters.mobile}
                                    onChange={handleFilterChange}
                                    placeholder="Search mobile"
                                />
                            </div>
                            <div className="col-xxl-2 col-sm-4">
                                <label className="form-label">Email</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="email"
                                    value={filters.email}
                                    onChange={handleFilterChange}
                                    placeholder="Search email"
                                />
                            </div>
                            <div className="col-xxl-2 col-sm-4">
                                <label className="form-label">Location</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="location"
                                    value={filters.location}
                                    onChange={handleFilterChange}
                                    placeholder="Search location"
                                />
                            </div>
                            <div className="col-xxl-4 col-sm-8">
                                <label className="form-label">Registration Date Range</label>
                                <Flatpickr
                                    className="form-control"
                                    placeholder="Select Registration Date Range"
                                    value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : (filters.startDate ? [filters.startDate] : [])}
                                    options={{
                                        mode: 'range',
                                        dateFormat: 'Y-m-d'
                                    }}
                                    onChange={(dates) => {
                                        if (dates.length === 2) {
                                            setFilters(prev => ({
                                                ...prev,
                                                startDate: getLocalDateString(dates[0]),
                                                endDate: getLocalDateString(dates[1])
                                            }));
                                            setPage(1);
                                        } else if (dates.length === 0) {
                                            setFilters(prev => ({
                                                ...prev,
                                                startDate: '',
                                                endDate: ''
                                            }));
                                            setPage(1);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive table-card mb-1">
                                    <table className="table align-middle table-nowrap" id="customerTable">
                                        <thead className="table-light text-muted">
                                            <tr>
                                                <th>Sr. No</th>
                                                <th>Customer Name</th>
                                                <th>Mobile</th>
                                                <th>Email ID</th>
                                                <th>Location</th>
                                                <th>Date</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="list form-check-all">
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : customers.length > 0 ? (
                                                customers.map((customer, index) => (
                                                    <tr key={customer.id}>
                                                        <td>{(page - 1) * limit + index + 1}</td>
                                                        <td>{customer.name || 'Guest'}</td>
                                                        <td>{customer.phone || 'N/A'}</td>
                                                        <td>{customer.email || 'N/A'}</td>
                                                        <td>{customer.location || 'N/A'}</td>
                                                        <td>{format(new Date(customer.createdAt), 'dd-MMM-yyyy')}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-soft-danger"
                                                                title="Delete Customer"
                                                                onClick={() => handleDeleteClick(customer)}
                                                            >
                                                                <i className="ri-delete-bin-fill align-bottom"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <div className="noresult">
                                                            <div className="text-center">
                                                                <h5 className="mt-2">Sorry! No Result Found</h5>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Pagination */}
                                {totalPages > 0 && (
                                    <div className="d-flex justify-content-end mt-3">
                                        <div className="pagination-wrap hstack gap-2">
                                            <button 
                                                className={`page-item pagination-prev ${page === 1 ? 'disabled' : ''}`}
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                            >
                                                Previous
                                            </button>
                                            <ul className="pagination listjs-pagination mb-0">
                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                                    <li key={p} className={p === page ? "active" : ""}>
                                                        <button className="page" onClick={() => setPage(p)}>{p}</button>
                                                    </li>
                                                ))}
                                            </ul>
                                            <button 
                                                className={`page-item pagination-next ${page === totalPages ? 'disabled' : ''}`}
                                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <Modal
                    show={showDeleteModal}
                    onHide={handleCloseDeleteModal}
                    centered
                    backdrop="static"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                    </Modal.Header>
                    <Modal.Body className="text-center pt-0 px-4 pb-4">
                        <div className="text-danger mb-3">
                            <div
                                className="d-inline-flex align-items-center justify-content-center bg-danger-subtle rounded-circle"
                                style={{ width: '70px', height: '70px' }}
                            >
                                <i className="ri-delete-bin-line" style={{ fontSize: '32px' }}></i>
                            </div>
                        </div>
                        <h4 className="mb-2">Are you sure?</h4>
                        <p className="text-muted mb-4">
                            Do you really want to delete customer{' '}
                            <strong>{customerToDelete?.name || 'Guest'}</strong>
                            {customerToDelete?.phone ? ` (${customerToDelete.phone})` : ''}?
                            <br />
                            <span className="text-danger fw-medium" style={{ fontSize: '13px' }}>
                                This action cannot be undone and will permanently remove this customer.
                            </span>
                        </p>
                        <div className="d-flex justify-content-center gap-2">
                            <button
                                type="button"
                                className="btn btn-light px-4"
                                onClick={handleCloseDeleteModal}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger px-4"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    'Yes, Delete Permanently'
                                )}
                            </button>
                        </div>
                    </Modal.Body>
                </Modal>

            </div>
        </div>
    );
};

export default RegisteredCustomers;

